import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Card,
  CardContent,
  GridLegacy as Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link as MuiLink,
  Paper,
  InputAdornment,
  Tabs,
  Tab,
  CardActionArea,
  Dialog,
  IconButton,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Phone as PhoneIcon,
  Link as LinkIcon,
  School as SchoolIcon,
  Description as DescriptionIcon,
  AttachMoney as MoneyIcon,
  Support as SupportIcon,
  Lightbulb as LightbulbIcon,
  PlayCircleOutline as PlayIcon,
  Close as CloseIcon,
  OndemandVideo as VideoLibraryIcon,
} from '@mui/icons-material';

interface TrainingVideo {
  id: number;
  title: string;
  description: string;
  videoPath: string;
  thumbnail: string;
  duration: string;
  category: string;
  color: string;
  isLoom?: boolean;
  isYouTube?: boolean;
  links?: { label: string; url: string }[];
}

interface LinkItem {
  label: string;
  value: string;
  description?: string;
}

interface ContentItem {
  type: 'alert' | 'text' | 'pricing' | 'subsection';
  severity?: 'error' | 'warning' | 'info' | 'success';
  title?: string;
  text?: string;
  online?: string;
  discount?: string;
  steps?: string[];
  items?: LinkItem[];
}

interface KnowledgeSection {
  id: string;
  title: string;
  icon: React.ReactElement;
  color: string;
  content: ContentItem[];
}

type VideoCategory = 'Onboarding' | 'Training Calls';

const KnowledgePortal: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [expandedSection, setExpandedSection] = useState<string | null>('getting-started');
  const [currentTab, setCurrentTab] = useState<number>(0);
  const [selectedVideo, setSelectedVideo] = useState<TrainingVideo | null>(null);
  const [videoDialogOpen, setVideoDialogOpen] = useState<boolean>(false);
  const [videoCategory, setVideoCategory] = useState<VideoCategory>('Onboarding');

  // Training Videos Configuration
  const trainingVideos: TrainingVideo[] = [
    {
      id: 1,
      title: 'Soniq Pulse Overview',
      description: 'Get started with Soniq Pulse and learn the fundamentals of the platform',
      videoPath: '/videos/OnboardingSoniqPulse.mp4',
      thumbnail: '/videos/OnboardingSoniqPulse.mp4',
      duration: '5:30',
      category: 'Onboarding',
      color: '#4B88A2',
    },
    {
      id: 2,
      title: 'Systems Overview',
      description: 'Learn about all the systems you\'ll be working with including CRM, dialer, and more',
      videoPath: '/videos/OnboardingSoniqSystems.mp4',
      thumbnail: '/videos/OnboardingSoniqSystems.mp4',
      duration: '8:45',
      category: 'Onboarding',
      color: '#2196F3',
    },
    {
      id: 3,
      title: 'Call Flow',
      description: 'Master the proper call flow and sales process from start to finish',
      videoPath: '/videos/OnboardingSoniqCallFlow.mp4',
      thumbnail: '/videos/OnboardingSoniqCallFlow.mp4',
      duration: '12:20',
      category: 'Onboarding',
      color: '#7B1FA2',
    },
    {
      id: 4,
      title: 'Five9 Dispositions',
      description: 'Learn how to properly use Five9 dispositions and track your calls',
      videoPath: '/videos/OnboardingFive9Dispositions.mp4',
      thumbnail: '/videos/OnboardingFive9Dispositions.mp4',
      duration: '6:15',
      category: 'Onboarding',
      color: '#F57C00',
    },
    {
      id: 5,
      title: 'What to Do When I Make a Sale',
      description: 'Complete post-sale process including intake forms and customer follow-up',
      videoPath: '/videos/OnboardingSoniqWinaDeal.mp4',
      thumbnail: '/videos/OnboardingSoniqWinaDeal.mp4',
      duration: '10:30',
      category: 'Onboarding',
      color: '#4CAF50',
    },
    {
      id: 6,
      title: 'Followups and Sending Links',
      description: 'Best practices for follow-ups and texting links to maximize conversions',
      videoPath: '/videos/OnboardingSoniqFollowups.mp4',
      thumbnail: '/videos/OnboardingSoniqFollowups.mp4',
      duration: '7:40',
      category: 'Onboarding',
      color: '#E91E63',
    },
    {
      id: 7,
      title: 'Escalations',
      description: 'Complete escalation procedures for shipping and customer service issues',
      videoPath: '/videos/SoniqCXEscalations.mp4',
      thumbnail: '/videos/SoniqCXEscalations.mp4',
      duration: '10:00',
      category: 'Onboarding',
      color: '#FF5722',
      links: [
        {
          label: 'Shipping Escalation',
          url: 'https://forms.zohopublic.com/directmeds1/form/Escalations/formperma/JTJsfESv6_92KzR8pkWLzILWNnAHUUOm0SwmHLuCd6U?zf_lang=en'
        },
        {
          label: 'CS/Nursing Escalation',
          url: 'https://forms.zohopublic.com/directmeds1/form/NursingCSTelesalesRequests/formperma/IaS4zhJvXgNRBFBK5R6uDM-l7fwet4D7aCq5OgVdEhY'
        }
      ]
    },
    {
      id: 8,
      title: 'Intake Form',
      description: 'Learn how to properly guide customers through the intake form process',
      videoPath: 'https://www.loom.com/embed/c934973d15744b75859915690253532d',
      thumbnail: 'https://cdn.loom.com/sessions/thumbnails/c934973d15744b75859915690253532d-with-play.gif',
      duration: '5:00',
      category: 'Onboarding',
      color: '#9C27B0',
      isLoom: true
    },
    {
      id: 9,
      title: 'Sales Training Call 1',
      description: 'January 28, 2026 - Real sales training call demonstrating best practices and techniques',
      videoPath: 'https://www.youtube.com/embed/5ZpPQNCprZc',
      thumbnail: 'https://img.youtube.com/vi/5ZpPQNCprZc/maxresdefault.jpg',
      duration: 'TBD',
      category: 'Training Calls',
      color: '#00897B',
      isYouTube: true,
    },
  ];

  const handleVideoClick = (video: TrainingVideo) => {
    setSelectedVideo(video);
    setVideoDialogOpen(true);
  };

  const handleCloseVideo = () => {
    setVideoDialogOpen(false);
    setSelectedVideo(null);
  };

  // Knowledge Base Sections
  const knowledgeBase: KnowledgeSection[] = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: <SchoolIcon />,
      color: '#4B88A2',
      content: [
        {
          type: 'alert',
          severity: 'info',
          title: 'Welcome to Direct Meds!',
          text: 'This portal contains everything you need to succeed. Please read through each section carefully.',
        },
        {
          type: 'subsection',
          title: 'Essential Links & Tools',
          items: [
            {
              label: 'Direct Meds Portal',
              value: 'https://portal.directmeds.com/',
              description: 'Customer portal for orders and intake forms',
            },
            {
              label: 'Zoho CRM',
              value: 'https://www.zoho.com/',
              description: 'Customer relationship management system',
            },
            {
              label: 'Five9 Dialer',
              value: 'https://login.five9.com/',
              description: 'Phone system login',
            },
            {
              label: 'Five9 Chrome Adapter',
              value: 'https://chromewebstore.google.com/detail/five9-plus-adapter-for-ag/mhlnodcncaldmcjkkllohonbgigbjpkb',
              description: 'Required Chrome extension for Five9',
            },
            {
              label: 'HIPAA Training Portal',
              value: 'https://www.hipaatraining.com/login',
              description: 'Username: DirectMeds2024 | Password: RedYellow32#',
            },
            {
              label: 'Medication Pricing Guide',
              value: 'https://docs.google.com/spreadsheets/d/1twGvQwKtICSw0RC4DitZCgZdCbrWxcEW37DEkEWJ7NA/edit?gid=1811559976#gid=1811559976',
              description: 'Current pricing for all medications',
            },
            {
              label: 'Soniq CX Dashboard',
              value: 'https://soniq-dashboard.onrender.com/',
              description: 'View commissions, performance metrics, and team rankings',
            },
          ],
        },
      ],
    },
    {
      id: 'escalation',
      title: 'Escalation Path',
      icon: <SupportIcon />,
      color: '#F57C00',
      content: [
        {
          type: 'alert',
          severity: 'warning',
          title: 'Escalation Hierarchy',
          text: 'Ben → Mike → Preston → Justin',
        },
        {
          type: 'text',
          text: 'It doesn\'t hurt to ping Ben and Mike. DO NOT ping Preston or Justin unless Ben or Mike are unavailable.',
        },
        {
          type: 'subsection',
          title: 'Support Numbers',
          items: [
            { label: 'Customer Support', value: '833-949-8998' },
            { label: 'Alternative Support', value: '888-696-7176' },
            { label: 'Pharmacy', value: 'Greenwichrx.net' },
          ],
        },
      ],
    },
    {
      id: 'internal-transfer',
      title: 'Internal Transfer Guidelines',
      icon: <PhoneIcon />,
      color: '#7B1FA2',
      content: [
        {
          type: 'alert',
          severity: 'info',
          title: 'Know Where to Transfer',
          text: 'Use this guide to quickly route calls to the correct department or team member.',
        },
        {
          type: 'subsection',
          title: 'Current Customers',
          steps: [
            '🔄 Updating a credit card on file → Billing Decline',
            '📝 Help completing an intake form → Intake Renewals Team',
            '❌ Wants to cancel → Loyalty Team (Cancellations)',
            '📞 Issues related to telesales interactions → Dedicated Telesales Escalations',
            '🛠️ All other account or order issues → General Customer Service',
          ],
        },
        {
          type: 'subsection',
          title: 'New and Potential Customers',
          steps: [
            '🇪🇸 Spanish-speaking only → Spanish Queue',
            '👤 Requests a specific sales rep → Transfer directly to that rep (type in their first name and it should pop up)',
          ],
        },
      ],
    },
    {
      id: 'sales-process',
      title: 'Suggested Call Flow Best Practices',
      icon: <DescriptionIcon />,
      color: '#2196F3',
      content: [
        {
          type: 'subsection',
          title: 'Opening Script Flow',
          text: 'It is suggested not to ask how they are or say "hello is this [customer]" - it is more effective to get right to the point. Hello? [Customer], this is [your name] calling with Direct Meds. It looks like you were online trying to put an order in for our GLP1 weight loss medication and it was abandoned, so I wanted to see if I could help you get that order completed.',
        },
        {
          type: 'subsection',
          title: 'Alternative Opener',
          text: 'Hello [Customer], this is [your name] from Direct Meds. I am calling you because you filled out a form inquiring about our GLP1 Medications.',
        },
        {
          type: 'subsection',
          title: 'If They Say It Was Too Expensive',
          steps: [
            'Ask if that was the price or because the price went up after the first month',
            'If they say it was because the price goes up, let them know you have a permanent discount code where the price will NOT go up',
            'Ask: "Out of curiosity, were you looking at the semaglutide or tirzepatide?"',
          ],
        },
        {
          type: 'pricing',
          title: 'Semaglutide Pricing',
          online: '$149/month first month → $299/month after',
          discount: '$179/month - LIFETIME DISCOUNT (never goes up)',
        },
        {
          type: 'pricing',
          title: 'Tirzepatide Pricing',
          online: '$349/month first month → $499/month after',
          discount: '$299/month - LIFETIME DISCOUNT (never goes up)',
        },
        {
          type: 'subsection',
          title: 'Six Key Questions (Discovery)',
          steps: [
            '1. Were you looking into the semaglutide or tirzepatide? Have you ever taken a GLP1 medication before?',
            '2. Out of curiosity... I work with people from 10 to 200 lb weight loss goals... what is your weight loss goal? (If you get this answer, sale probability increases significantly)',
            '3. What made you decide to look into GLP-1s right now?',
            '4. What\'s been the most frustrating part about trying to manage this on your own?',
            '5. Have you heard about how GLP-1s work differently than other weight loss methods? (Share stories, build confidence)',
            '6. Based on what we\'ve talked about today, do you feel like this could be a good fit for what you\'re trying to achieve?',
          ],
        },
        {
          type: 'alert',
          severity: 'success',
          title: 'Closing Question',
          text: 'If I could help you get started today, is that something you would want to move forward with?',
        },
        {
          type: 'text',
          text: 'If they feel hesitant, ask: "What would it mean for you personally if you could hit your weight target?" Then reaffirm that this will work for them.',
        },
      ],
    },
    {
      id: 'closing-sale',
      title: 'How to Close the Sale',
      icon: <CheckCircleIcon />,
      color: '#4CAF50',
      content: [
        {
          type: 'alert',
          severity: 'success',
          title: 'Critical: Reduce Cancellations',
          text: 'Following this exact process will significantly reduce cancellations!',
        },
        {
          type: 'subsection',
          title: 'After Getting Credit Card',
          text: 'Say the following: "I just want to confirm this is going to be shipped to [Customer Name] and the address we are shipping to is [Customer Address]. Your cost today is [Quote cost] - this is a LIFETIME DISCOUNT. We will be shipping 90 days up front (for new customers) but you will still only be billed monthly. It\'s important to understand that this medication takes 2 to 3 months to absorb, so you will see results during the first three months, but most people see the biggest results in months 3-8, so be patient. If you have any issues, you can call our support and they can connect you with a medical professional to discuss."',
        },
        {
          type: 'subsection',
          title: 'Immediately After Closing',
          steps: [
            'Text them the portal link: https://portal.directmeds.com/login',
            'Have them login to the intake form - DON\'T WAIT for email',
            'Tell them NOT to check the box for labs',
            'Inform them they need two pictures: Front of driver\'s license AND a clothed full-body picture to match the ID',
            'Let them know meds arrive within 3-5 days after doctor reviews and approves',
            'Give them support numbers: 833-949-8998 or 888-696-7176',
            'Once they start the medical intake, you can let them go',
          ],
        },
      ],
    },
    {
      id: 'texting-links',
      title: 'Texting Links Protocol',
      icon: <LinkIcon />,
      color: '#E91E63',
      content: [
        {
          type: 'alert',
          severity: 'info',
          title: 'Great tool when done properly!',
          text: 'Texting links can be effective, but you MUST follow these steps:',
        },
        {
          type: 'subsection',
          title: 'Required Steps for Texting Links',
          steps: [
            '1. Convert the lead into YOUR name',
            '2. Convert the lead so it has a deal',
            '3. Make sure the customer knows YOUR link has the discount - they MUST use your link',
            '4. Only do this with people you talk to that have a higher chance of using it - don\'t just text everyone',
          ],
        },
        {
          type: 'alert',
          severity: 'error',
          title: 'No Payment on Wrong Link Sales',
          text: 'Starting on the 15th, Direct Meds will NOT pay us on sales when customers use the wrong link. Following the steps above will ensure you get paid!',
        },
      ],
    },
    {
      id: 'compliance',
      title: 'Compliance & Wording',
      icon: <WarningIcon />,
      color: '#D32F2F',
      content: [
        {
          type: 'alert',
          severity: 'error',
          title: 'NEVER Use These Phrases',
          text: 'Compliance violations can result in termination and legal issues.',
        },
        {
          type: 'subsection',
          title: '❌ FORBIDDEN WORDING',
          steps: [
            '❌ "It\'s only a 1-month commitment"',
            '❌ Any guarantee of weight loss',
            '❌ Any wording that doesn\'t clearly communicate this is a 90-day commitment',
          ],
        },
        {
          type: 'subsection',
          title: '✅ CORRECT APPROACH',
          steps: [
            '✅ Clearly state it\'s a 90-day initial supply',
            '✅ Explain lifetime discount pricing',
            '✅ Set realistic expectations (2-3 months to absorb, best results in months 3-8)',
            '✅ Never promise specific weight loss amounts',
          ],
        },
      ],
    },
    {
      id: 'payments',
      title: 'Payment Processing',
      icon: <MoneyIcon />,
      color: '#00BCD4',
      content: [
        {
          type: 'subsection',
          title: 'HSA & CareCredit Cards',
          text: 'If HSA cards are denied, management can try processing them manually.',
        },
        {
          type: 'alert',
          severity: 'warning',
          title: 'CareCredit Instructions',
          text: 'Please try ALL CareCredit cards! If declined, submit the info below to your manager and pivot to another payment method.',
        },
        {
          type: 'subsection',
          title: 'CareCredit Decline Information to Submit',
          steps: [
            '1. Date/time (with timezone)',
            '2. Customer name',
            '3. Card brand: CareCredit',
            '4. First 6 digits of the card number',
            '5. Screenshot of the page with browser address bar where error/decline occurred',
            'OR Portal link to the failed payment attempt',
          ],
        },
      ],
    },
    {
      id: 'cancellation-prevention',
      title: 'Eliminate Cancellations',
      icon: <LightbulbIcon />,
      color: '#FF5722',
      content: [
        {
          type: 'alert',
          severity: 'success',
          title: 'Goal: Zero First Week & First Month Cancellations',
          text: 'This comes down to getting them to complete the intake form immediately!',
        },
        {
          type: 'subsection',
          title: 'Post-Sale Protocol (CRITICAL)',
          steps: [
            '✅ Text them https://portal.directmeds.com/login immediately',
            '✅ Have them login right away - DON\'T wait for email',
            '✅ Tell them NOT to check the box for labs',
            '✅ Explain they need: Front of driver\'s license photo + Clothed full-body picture',
            '✅ Inform them meds arrive 3-5 days after doctor approval',
            '✅ Give them support numbers: 833-949-8998 or 888-696-7176',
            '✅ Once they start the intake form, you can disconnect',
          ],
        },
        {
          type: 'text',
          text: 'Following this protocol will nearly eliminate cancellations!',
        },
      ],
    },
    {
      id: 'w9-taxes',
      title: 'W9 and Taxes',
      icon: <MoneyIcon />,
      color: '#795548',
      content: [
        {
          type: 'alert',
          severity: 'warning',
          title: 'Independent Contractor Status',
          text: 'As an independent contractor, you are responsible for managing your own taxes.',
        },
        {
          type: 'subsection',
          title: 'Tax Responsibilities',
          steps: [
            '✅ You are responsible for filing your own taxes',
            '✅ You will receive a 1099 form if you earn $600 or more in a calendar year',
            '✅ It is recommended to set aside 25-30% of your earnings for taxes',
            '✅ You may need to make quarterly estimated tax payments to the IRS',
            '✅ Consider consulting with a tax professional for guidance',
          ],
        },
        {
          type: 'subsection',
          title: 'W9 Form',
          text: 'You will be required to complete a W9 form for tax reporting purposes. This form provides your taxpayer identification information and certifies that you are not subject to backup withholding.',
        },
        {
          type: 'alert',
          severity: 'info',
          title: 'Important Reminder',
          text: 'Keep accurate records of all your income and business-related expenses throughout the year. This will make tax filing much easier and may help you qualify for deductions.',
        },
      ],
    },
    {
      id: 'faqs',
      title: 'Frequently Asked Questions',
      icon: <InfoIcon />,
      color: '#607D8B',
      content: [
        {
          type: 'subsection',
          title: 'Direct Meds FAQ Resources',
          items: [
            {
              label: 'Official FAQ Page',
              value: 'https://directmeds.com/dm-q2spc-u/?oid=12&uid=71&affid=362',
              description: 'Comprehensive FAQ for customer questions',
            },
          ],
        },
        {
          type: 'subsection',
          title: 'Common Customer Questions',
          steps: [
            'Q: How long until I see results? A: 2-3 months to absorb, best results months 3-8',
            'Q: Can I cancel anytime? A: Initial commitment is 90 days',
            'Q: Does the price increase? A: With your discount code, NO - lifetime pricing',
            'Q: Is Direct Meds FDA approved? A: Direct Meds is a telehealth service, which doesn\'t require FDA approval. FDA approval applies to medications—not healthcare platforms. We operate just like other major telehealth providers—licensed clinicians prescribe, and licensed pharmacies dispense.',
            'Q: Are compounded medications FDA approved? A: No—compounded medications are not FDA approved. However, they are legal when prescribed by a licensed provider and prepared by regulated compounding pharmacies. Compounded medications are widely used in healthcare when patients need individualized formulations, and our partner pharmacies follow strict safety standards.',
            'Q: Do I need a prescription? A: Yes, our telemedicine doctors will review your intake',
            'Q: How is it shipped? A: Discreet packaging, arrives in 3-5 days after approval',
          ],
        },
      ],
    },
  ];

  // Filter sections based on search
  const filteredSections = knowledgeBase.filter((section) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    
    // Search in title
    if (section.title.toLowerCase().includes(searchLower)) return true;
    
    // Search in content
    const contentMatch = section.content.some((item) => {
      if (item.text && item.text.toLowerCase().includes(searchLower)) return true;
      if (item.title && item.title.toLowerCase().includes(searchLower)) return true;
      if (item.steps && item.steps.some(step => step.toLowerCase().includes(searchLower))) return true;
      if (item.items && item.items.some(i => 
        (i.label && i.label.toLowerCase().includes(searchLower)) ||
        (i.value && i.value.toLowerCase().includes(searchLower)) ||
        (i.description && i.description.toLowerCase().includes(searchLower))
      )) return true;
      return false;
    });
    
    return contentMatch;
  });

  const renderContent = (item: ContentItem) => {
    switch (item.type) {
      case 'alert':
        return (
          <Alert severity={item.severity} sx={{ mb: 2 }}>
            {item.title && <Typography variant="subtitle2" fontWeight="bold">{item.title}</Typography>}
            <Typography variant="body2">{item.text}</Typography>
          </Alert>
        );
      
      case 'text':
        return (
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
            {item.text}
          </Typography>
        );
      
      case 'pricing':
        return (
          <Paper elevation={2} sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
            <Typography variant="h6" gutterBottom color="primary">
              {item.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>Online Price:</strong> {item.online}
            </Typography>
            <Typography variant="body1" color="success.main" fontWeight="bold">
              <strong>Your Discount:</strong> {item.discount}
            </Typography>
          </Paper>
        );
      
      case 'subsection':
        return (
          <Box sx={{ mb: 3 }}>
            {item.title && (
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                {item.title}
              </Typography>
            )}
            {item.text && (
              <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7, fontStyle: 'italic', bgcolor: '#f0f7ff', p: 2, borderRadius: 1 }}>
                "{item.text}"
              </Typography>
            )}
            {item.steps && (
              <List>
                {item.steps.map((step, idx) => (
                  <ListItem key={idx} sx={{ alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleIcon color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary={step}
                      primaryTypographyProps={{
                        variant: 'body1',
                        sx: { lineHeight: 1.7 }
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
            {item.items && (
              <List>
                {item.items.map((listItem, idx) => (
                  <ListItem key={idx} sx={{ alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {listItem.value.startsWith('http') ? <LinkIcon color="primary" /> : <PhoneIcon color="primary" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" fontWeight="bold">
                          {listItem.label}
                        </Typography>
                      }
                      secondary={
                        <>
                          {listItem.value.startsWith('http') ? (
                            <MuiLink href={listItem.value} target="_blank" rel="noopener" sx={{ wordBreak: 'break-all' }}>
                              {listItem.value}
                            </MuiLink>
                          ) : (
                            <Typography variant="body2" color="text.primary" fontWeight="500">
                              {listItem.value}
                            </Typography>
                          )}
                          {listItem.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {listItem.description}
                            </Typography>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        );
      
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom fontWeight="bold" sx={{ color: '#2C3E50' }}>
          Knowledge Portal
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Your Complete Guide to Success at Direct Meds
        </Typography>
      </Box>

      {/* Tab Navigation */}
      <Paper sx={{ mb: 3, bgcolor: '#2C3E50' }} elevation={2}>
        <Tabs
          value={currentTab}
          onChange={(_, newValue: number) => setCurrentTab(newValue)}
          sx={{
            '& .MuiTab-root': {
              color: 'rgba(255, 255, 255, 0.7)',
              fontWeight: 600,
              fontSize: '1rem',
              textTransform: 'none',
              minHeight: 64,
            },
            '& .Mui-selected': {
              color: '#ffffff !important',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#4B88A2',
              height: 4,
            },
          }}
        >
          <Tab icon={<VideoLibraryIcon />} iconPosition="start" label="Training Videos" />
          <Tab icon={<SchoolIcon />} iconPosition="start" label="Knowledge Base" />
        </Tabs>
      </Paper>

      {/* Training Videos Tab */}
      {currentTab === 0 && (
        <Box>
          {/* Training Videos Header with Toggle */}
          <Paper sx={{ p: 3, mb: 3, bgcolor: '#f8f9fa', borderLeft: '4px solid #4B88A2' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ color: '#2C3E50' }}>
                  📹 Onboarding and Training Video Library
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Watch these essential training videos to master your role
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Card
                  onClick={() => setVideoCategory('Onboarding')}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: videoCategory === 'Onboarding' ? '#4B88A2' : 'white',
                    color: videoCategory === 'Onboarding' ? 'white' : '#2C3E50',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 3,
                    },
                  }}
                >
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="body2" fontWeight={600}>
                      🎓 Onboarding ({trainingVideos.filter(v => v.category === 'Onboarding').length})
                    </Typography>
                  </CardContent>
                </Card>
                <Card
                  onClick={() => setVideoCategory('Training Calls')}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: videoCategory === 'Training Calls' ? '#00897B' : 'white',
                    color: videoCategory === 'Training Calls' ? 'white' : '#2C3E50',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 3,
                    },
                  }}
                >
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="body2" fontWeight={600}>
                      📞 Training Calls ({trainingVideos.filter(v => v.category === 'Training Calls').length})
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          </Paper>

          {/* Video Grid - Shows selected category only */}
          <Box>
            <Grid container spacing={3}>
              {trainingVideos.filter(v => v.category === videoCategory).map((video) => (
                <Grid item xs={12} sm={6} md={4} key={video.id}>
                  <Card 
                    elevation={3}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.3s',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 6,
                      },
                    }}
                    onClick={() => handleVideoClick(video)}
                  >
                    <CardActionArea>
                      <Box sx={{ position: 'relative', paddingTop: '56.25%', bgcolor: video.color }}>
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'rgba(0, 0, 0, 0.4)',
                          }}
                        >
                          <PlayIcon sx={{ fontSize: 80, color: 'white', opacity: 0.9 }} />
                        </Box>
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            bgcolor: 'rgba(0, 0, 0, 0.7)',
                            color: 'white',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.875rem',
                            fontWeight: 600,
                          }}
                        >
                          {video.duration}
                        </Box>
                      </Box>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography
                          gutterBottom
                          variant="h6"
                          component="div"
                          fontWeight="bold"
                          sx={{ color: '#2C3E50' }}
                        >
                          {video.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          {video.description}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      )}

      {/* Knowledge Base Tab */}
      {currentTab === 1 && (
        <Box>
          {/* Search Bar */}
          <Paper elevation={2} sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa' }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search the knowledge base..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#4B88A2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                bgcolor: 'white',
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#4B88A2',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#4B88A2',
                  },
                },
              }}
            />
          </Paper>

          {/* Quick Access Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Card elevation={2} sx={{ bgcolor: '#4B88A2', color: 'white' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <PhoneIcon sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6" fontWeight="bold">
                    833-949-8998
                  </Typography>
                  <Typography variant="body2">
                    Customer Support
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card elevation={2} sx={{ bgcolor: '#2196F3', color: 'white' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <LinkIcon sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="body1" fontWeight="bold" sx={{ wordBreak: 'break-all' }}>
                    portal.directmeds.com
                  </Typography>
                  <Typography variant="body2">
                    Customer Portal
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card elevation={2} sx={{ bgcolor: '#4CAF50', color: 'white' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <SchoolIcon sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6" fontWeight="bold">
                    {knowledgeBase.length}
                  </Typography>
                  <Typography variant="body2">
                    Knowledge Sections
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Knowledge Base Sections */}
          <Box>
            {filteredSections.length === 0 ? (
              <Alert severity="info">
                No results found for "{searchTerm}". Try a different search term.
              </Alert>
            ) : (
              filteredSections.map((section) => (
                <Accordion
                  key={section.id}
                  expanded={expandedSection === section.id}
                  onChange={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
                  sx={{ 
                    mb: 2,
                    '&:before': {
                      display: 'none',
                    },
                    boxShadow: 2,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                    sx={{
                      bgcolor: section.color,
                      color: 'white',
                      '&:hover': { 
                        bgcolor: section.color,
                        filter: 'brightness(1.1)',
                      },
                      minHeight: 64,
                      '& .MuiAccordionSummary-content': {
                        my: 1.5,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {section.icon}
                      <Typography variant="h6" fontWeight="bold">
                        {section.title}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                    {section.content.map((item, idx) => (
                      <Box key={idx}>
                        {renderContent(item)}
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </Box>
        </Box>
      )}

      {/* Video Dialog */}
      <Dialog
        open={videoDialogOpen}
        onClose={handleCloseVideo}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#2C3E50',
          }
        }}
      >
        <Box sx={{ position: 'relative', bgcolor: '#2C3E50' }}>
          <IconButton
            onClick={handleCloseVideo}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'white',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1,
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.7)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          {selectedVideo && (
            <Box>
              <Box sx={{ p: 3, pb: 2 }}>
                <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ color: 'white' }}>
                  {selectedVideo.title}
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  {selectedVideo.description}
                </Typography>
              </Box>
              <Box sx={{ bgcolor: 'black', aspectRatio: '16/9' }}>
                {selectedVideo.isLoom || selectedVideo.isYouTube ? (
                  <iframe
                    src={selectedVideo.videoPath}
                    frameBorder="0"
                    allowFullScreen
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    style={{
                      width: '100%',
                      height: '100%',
                      display: 'block',
                    }}
                    title={selectedVideo.title}
                  ></iframe>
                ) : (
                  <video
                    controls
                    autoPlay
                    style={{
                      width: '100%',
                      height: '100%',
                      display: 'block',
                    }}
                  >
                    <source src={selectedVideo.videoPath} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                )}
              </Box>
              {selectedVideo.links && selectedVideo.links.length > 0 && (
                <Box sx={{ p: 3, bgcolor: '#2C3E50' }}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'white', mb: 2 }}>
                    📋 Related Resources
                  </Typography>
                  <Grid container spacing={2}>
                    {selectedVideo.links.map((link, idx) => (
                      <Grid item xs={12} sm={6} key={idx}>
                        <Card 
                          sx={{ 
                            bgcolor: '#34495E',
                            '&:hover': {
                              bgcolor: '#3F536B',
                            },
                            transition: 'all 0.3s',
                          }}
                        >
                          <CardActionArea 
                            component="a" 
                            href={link.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            <CardContent>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <LinkIcon sx={{ fontSize: 32, color: '#4B88A2' }} />
                                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                                  {link.label}
                                </Typography>
                              </Box>
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Dialog>

      {/* Footer */}
      <Box sx={{ mt: 6, p: 3, bgcolor: '#2C3E50', color: 'white', borderRadius: 2, textAlign: 'center' }}>
        <Typography variant="body2">
          Last Updated: January 2026 | For questions or updates, contact your manager
        </Typography>
      </Box>
    </Container>
  );
};

export default KnowledgePortal;
